import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import {
  filter,
  Observable,
  of,
  ReplaySubject,
  switchMap,
  tap,
  Subscription,
  concat,
  toArray,
  map,
  forkJoin
} from "rxjs";
import { TranslateService } from "@ngx-translate/core";
import { FuseNavigationItem } from "@fuse/components/navigation";
import { Navigation } from "app/core/navigation/navigation.types";
import { navigation } from "./navigation";
import { AccountService } from "../services/account/account.service";
import { RosterService } from "../services/roster/roster.service";
import { UtilService } from "../services/utils/util.service";
import { environment } from "environments/environment";
import { BadgesService } from "../services/badges/badges.service";
import { LaunchDarklyService } from "app/launchdarkly.service";

@Injectable({
  providedIn: "root"
})
export class NavigationService {
  private _navigation = new ReplaySubject<Navigation>(1);
  private accountSub!: Subscription;

  private readonly currentYear: number;
  private readonly currentWeek: number;

  private badgeMap: Record<string, string> = {};
  shifts: any[] = [];
  currentAccount: any;
  resources: any[] = [];
  resourceInvites: any[] = [];
  concatResources: any = null;

  /**
   * Constructor
   */
  constructor(
    private _httpClient: HttpClient,
    private accountService: AccountService,
    private _rosterService: RosterService,
    private _badgeService: BadgesService,
    private _utilService: UtilService,
    private ld: LaunchDarklyService,
    private translate: TranslateService
  ) {
    const today = new Date();
    this.currentYear = today.getFullYear();
    this.currentWeek = this._utilService.getIsoWeekNumber(today);

    this.subscribeToAccountStream();
    this.ld.flags$.subscribe(() => this.publishNavigation());
    this.translate.onLangChange.subscribe(() => this.publishNavigation());
  }

  private isFlagOn = (key: string, def = false) => this.ld.getFlag(key, def);

  public refreshForAccount(): void {
    if (this.accountSub) {
      this.accountSub.unsubscribe();
    }
    this.subscribeToAccountStream();
  }

  private subscribeToAccountStream(): void {
    this.accountSub = this.accountService.currentAccount
      .pipe(
        filter(
          (acc: any) =>
            acc && typeof acc.uuid === "string" && acc.uuid.length > 0
        ),
        tap((acc) => {
          this.currentAccount = acc;
          this.badgeMap["clients"] = this._badgeService.navBadges(
            acc,
            "clients"
          );
          this.badgeMap["suppliers"] = this._badgeService.navBadges(
            acc,
            "suppliers"
          );
          this.badgeMap["resources"] = this._badgeService.navBadges(
            acc,
            "resources"
          );
          this.badgeMap["account-details"] = this._badgeService.navBadges(
            acc,
            "account-details"
          );
        }),
        switchMap((acc: any) => {
          if (acc?.verification?.status !== 'accepted') {
            return of([]);
          }
          return this._rosterService
            .getShiftsForPrimaryAccount(
              acc.uuid,
              this.currentYear,
              this.currentWeek
            )
            .pipe(
              switchMap((shiftList: any[]) => {
                if (shiftList.length === 0) {
                  return of(shiftList);
                }
                const ts = Math.floor(Date.now() / 1000);
                const tzCalls = shiftList.map((shift) => {
                  const coords =
                    shift.venue?.address?.geo_location?.coordinates;
                  if (
                    !coords ||
                    !Array.isArray(coords) ||
                    coords.length !== 2
                  ) {
                    return;
                  }
                  const [lng, lat] = coords as [number, number];

                  if (Array.isArray(coords) && coords.length === 2) {
                    return this._rosterService.getTimeZoneFromGeo(
                      [lng, lat],
                      ts
                    );
                  } else {
                    return of(null);
                  }
                });

                return forkJoin(tzCalls).pipe(
                  tap((timezoneResponses) => {
                    shiftList.forEach((shift, i) => {
                      shift.venueTime = timezoneResponses[i];
                    });
                  }),
                  map(() => shiftList)
                );
              })
            );
        })
      )
      .subscribe({
        next: (enrichedShifts: any[]) => {
          this.shifts = enrichedShifts;
          this.currentAccount["shifts"] = this.shifts;
          this._rosterService.processShiftsFromAccount(this.currentAccount);
          this.badgeMap["roster"] = this._badgeService.navBadges(
            this.currentAccount,
            "roster"
          );
          this.badgeMap["incident-reports"] = this._badgeService.navBadges(
            this.currentAccount,
            "incident-reports"
          );
          this.badgeMap["timesheet-register"] = this._badgeService.navBadges(
            this.currentAccount,
            "timesheet-register"
          );
          this.publishNavigation();
        },
        error: (err) => {
          console.error(
            "NavigationService: error fetching shifts or timezones",
            err
          );
          this.shifts = [];
          this.publishNavigation();
        }
      });
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for navigation
   */
  get navigation$(): Observable<Navigation> {
    return this._navigation.asObservable();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Private methods
  // -----------------------------------------------------------------------------------------------------

  // Navigation titles are translation keys; badge titles are counts and stay as-is.
  private translateItems(items: FuseNavigationItem[]): FuseNavigationItem[] {
    return items.map((item) => ({
      ...item,
      ...(item.title ? { title: this.translate.instant(item.title) } : {}),
      ...(item.children
        ? { children: this.translateItems(item.children) }
        : {})
    }));
  }

  private buildNavigation(): Navigation {
    const make = () =>
      this.translateItems(
        navigation(this.badgeMap, this.currentAccount, this.isFlagOn)
      );

    return {
      default: make(),
      compact: make(),
      futuristic: make(),
      horizontal: make()
    };
  }

  private publishNavigation(): void {
    this._navigation.next(this.buildNavigation());
  }
  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get all navigation data
   */
  get(): Observable<Navigation> {
    return of(this.buildNavigation()).pipe(
      tap((nav) => this._navigation.next(nav))
    );
  }
}
